import React, { useState, useEffect, useRef } from "react";
import { 
  Users, 
  Trash2,
  Loader2,
  Building2,
  UserPlus,
  Upload,
  Image,
  Crown,
  Mail,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Bell,
  Send,
  BellRing,
  Gift,
  FileText
} from "lucide-react";
import { Layout } from "../components/Layout";
import NotificationSettings from "../components/NotificationSettings";
import ReferralProgram from "../components/ReferralProgram";
import AuditLog from "../components/AuditLog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { apiRequest, formatDate } from "../lib/utils";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "../components/ToastProvider";
import { useAuth } from "../context/AuthContext";
import { AdminPasswordDialog } from "../components/AdminPasswordDialog";

// Labels dos planos
const PLANOS = {
  basico: { nome: "Básico", limite: 2, cor: "bg-slate-100 text-slate-700" },
  profissional: { nome: "Profissional", limite: 6, cor: "bg-blue-100 text-blue-700" },
  enterprise: { nome: "Enterprise", limite: 11, cor: "bg-purple-100 text-purple-700" },
};

export const Admin = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [planoInfo, setPlanoInfo] = useState(null);
  
  // Convite de usuários
  const [convidarDialogOpen, setConvidarDialogOpen] = useState(false);
  const [convidando, setConvidando] = useState(false);
  const [convidarForm, setConvidarForm] = useState({ nome: "", email: "", cargo: "" });
  const [senhaTemporaria, setSenhaTemporaria] = useState("");
  const [deleteUsuarioId, setDeleteUsuarioId] = useState(null);
  const [showDeletePasswordDialog, setShowDeletePasswordDialog] = useState(false);
  const [reenviandoConvite, setReenviandoConvite] = useState(null);
  
  // Logomarca
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  // Limpar dados
  const [limparDialogOpen, setLimparDialogOpen] = useState(false);
  const [limparSenha, setLimparSenha] = useState("");
  const [limparConfirmar, setLimparConfirmar] = useState(false);
  const [limpando, setLimpando] = useState(false);

  // Alertas de verificações
  const [alertasConfig, setAlertasConfig] = useState({
    ativo: false,
    enviar_admin: true,
    enviar_email_adicional: false,
    email_adicional: "",
    horario_manha: "12:00",
    horario_tarde: "18:00"
  });
  const [salvandoAlertas, setSalvandoAlertas] = useState(false);
  const [enviandoAlertaTeste, setEnviandoAlertaTeste] = useState(false);

  useEffect(() => {
    fetchAllData();
    fetchLogomarca();
    fetchAlertasConfig();
  }, []);

  const fetchLogomarca = async () => {
    try {
      const data = await apiRequest("/admin/logomarca");
      setLogoUrl(data.logo_url);
    } catch (error) {
      console.log("Sem logomarca cadastrada");
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/admin/logomarca`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Erro ao fazer upload");
      }
      
      const data = await response.json();
      setLogoUrl(data.url);
      toast.success("Logomarca atualizada com sucesso!");
    } catch (error) {
      toast.error(error.message || "Erro ao fazer upload da logomarca");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const fetchAlertasConfig = async () => {
    try {
      const data = await apiRequest("/admin/alertas-config");
      setAlertasConfig(data);
    } catch (error) {
      console.log("Erro ao carregar configuração de alertas:", error);
    }
  };

  const handleSalvarAlertas = async () => {
    setSalvandoAlertas(true);
    try {
      await apiRequest("/admin/alertas-config", {
        method: "PUT",
        body: JSON.stringify(alertasConfig)
      });
      toast.success("Configuração de alertas salva com sucesso!");
    } catch (error) {
      toast.error(error.message || "Erro ao salvar configuração");
    } finally {
      setSalvandoAlertas(false);
    }
  };

  const handleEnviarAlertaTeste = async () => {
    setEnviandoAlertaTeste(true);
    try {
      const result = await apiRequest("/admin/enviar-alerta-verificacoes", {
        method: "POST"
      });
      if (result.enviado) {
        toast.success(`Alerta enviado para ${result.enviados} destinatário(s)!`);
      } else {
        toast.info(result.motivo || "Nenhum alerta enviado");
      }
    } catch (error) {
      toast.error(error.message || "Erro ao enviar alerta");
    } finally {
      setEnviandoAlertaTeste(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await apiRequest("/admin/logomarca", { method: "DELETE" });
      setLogoUrl(null);
      toast.success("Logomarca removida com sucesso!");
    } catch (error) {
      toast.error("Erro ao remover logomarca");
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usuariosData, planoData] = await Promise.all([
        apiRequest("/admin/usuarios"),
        apiRequest("/admin/info-plano"),
      ]);
      setUsuarios(usuariosData.usuarios || usuariosData);
      setPlanoInfo(planoData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleConvidarUsuario = async () => {
    if (!convidarForm.nome || !convidarForm.email) {
      toast.error("Preencha nome e email");
      return;
    }
    
    setConvidando(true);
    try {
      const result = await apiRequest("/admin/convidar-usuario", {
        method: "POST",
        body: JSON.stringify(convidarForm)
      });
      
      setSenhaTemporaria(result.senha_temporaria);
      toast.success("Usuário convidado com sucesso!");
      fetchAllData();
    } catch (error) {
      toast.error(error.message || "Erro ao convidar usuário");
    } finally {
      setConvidando(false);
    }
  };

  const handleDeleteUsuarioClick = (usuarioId) => {
    setDeleteUsuarioId(usuarioId);
    setShowDeletePasswordDialog(true);
  };

  const handleRemoverUsuario = async () => {
    if (!deleteUsuarioId) return;
    
    try {
      await apiRequest(`/admin/usuarios/${deleteUsuarioId}`, {
        method: "DELETE"
      });
      toast.success("Usuário removido com sucesso!");
      setDeleteUsuarioId(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.message || "Erro ao remover usuário");
    }
  };

  const handleReenviarConvite = async (usuarioId) => {
    setReenviandoConvite(usuarioId);
    try {
      const result = await apiRequest(`/admin/usuarios/${usuarioId}/reenviar-convite`, {
        method: "POST"
      });
      toast.success(result.message || "Convite reenviado com sucesso!");
      // Mostrar a senha temporária
      if (result.senha_temporaria) {
        toast.info(`Senha temporária: ${result.senha_temporaria}`);
      }
    } catch (error) {
      toast.error(error.message || "Erro ao reenviar convite");
    } finally {
      setReenviandoConvite(null);
    }
  };

  const handleLimparDados = async () => {
    if (!limparSenha) {
      toast.error("Digite sua senha para confirmar");
      return;
    }
    if (!limparConfirmar) {
      toast.error("Marque a confirmação para continuar");
      return;
    }
    
    setLimpando(true);
    try {
      const result = await apiRequest("/admin/limpar-dados", {
        method: "POST",
        body: JSON.stringify({ senha: limparSenha, confirmar: limparConfirmar })
      });
      toast.success("Dados limpos com sucesso!");
      setLimparDialogOpen(false);
      setLimparSenha("");
      setLimparConfirmar(false);
      // Mostrar resumo do que foi excluído
      const dados = result.dados_excluidos;
      toast.info(`Excluídos: ${dados.documentos} documentos, ${dados.eventos} eventos, ${dados.funcionarios} funcionários, ${dados.itens_calibraveis} itens`);
    } catch (error) {
      toast.error(error.message || "Erro ao limpar dados");
    } finally {
      setLimpando(false);
    }
  };

  const planoAtual = planoInfo?.plano_atual || planoInfo?.plano || "basico";
  const planoConfig = PLANOS[planoAtual] || PLANOS.basico;
  // A listagem de usuários já inclui o admin, não precisa somar +1
  const usuariosCount = usuarios.length;
  // Usar max_usuarios do backend se disponível, senão usar o limite do plano local
  const limiteUsuarios = planoInfo?.max_usuarios || planoConfig.limite;
  const podeConvidar = usuariosCount < limiteUsuarios;

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 animate-pulse">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-slide-in" data-testid="admin-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Administração
          </h1>
          <p className="text-slate-500 mt-1">Gerencie sua farmácia e equipe</p>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {/* Card do Plano */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Seu Plano</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${planoConfig.cor}`}>
                      {planoConfig.nome}
                    </span>
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-purple-50 text-purple-600">
                  <Crown className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card de Usuários */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Usuários</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>
                    {usuariosCount} / {limiteUsuarios}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {podeConvidar ? `${limiteUsuarios - usuariosCount} vagas disponíveis` : "Limite atingido"}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-teal-50 text-teal-600">
                  <Users className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card da Logomarca */}
          <Card className="sm:col-span-2 md:col-span-1">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Logomarca</p>
                  <p className="text-sm mt-2">
                    {logoUrl ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> Configurada
                      </span>
                    ) : (
                      <span className="text-amber-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> Não configurada
                      </span>
                    )}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
                  <Image className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Simplificadas */}
        <Tabs defaultValue="usuarios" className="space-y-4">
          <TabsList className="w-full sm:w-auto flex flex-wrap">
            <TabsTrigger value="usuarios" className="flex items-center gap-2 flex-1 sm:flex-none" data-testid="tab-usuarios">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Usuários</span>
              <span className="sm:hidden">Usuários</span>
            </TabsTrigger>
            <TabsTrigger value="empresa" className="flex items-center gap-2 flex-1 sm:flex-none" data-testid="tab-empresa">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Minha Farmácia</span>
              <span className="sm:hidden">Farmácia</span>
            </TabsTrigger>
            <TabsTrigger value="alertas" className="flex items-center gap-2 flex-1 sm:flex-none" data-testid="tab-alertas">
              <Bell className="w-4 h-4" />
              <span className="hidden sm:inline">Alertas Email</span>
              <span className="sm:hidden">Alertas</span>
            </TabsTrigger>
            <TabsTrigger value="push" className="flex items-center gap-2 flex-1 sm:flex-none" data-testid="tab-push">
              <BellRing className="w-4 h-4" />
              <span className="hidden sm:inline">Notificações Push</span>
              <span className="sm:hidden">Push</span>
            </TabsTrigger>
            <TabsTrigger value="indicacao" className="flex items-center gap-2 flex-1 sm:flex-none" data-testid="tab-indicacao">
              <Gift className="w-4 h-4" />
              <span className="hidden sm:inline">Indicação</span>
              <span className="sm:hidden">Indicar</span>
            </TabsTrigger>
            <TabsTrigger value="auditoria" className="flex items-center gap-2 flex-1 sm:flex-none" data-testid="tab-auditoria">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Auditoria</span>
              <span className="sm:hidden">Logs</span>
            </TabsTrigger>
          </TabsList>

          {/* Aba Usuários */}
          <TabsContent value="usuarios">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Equipe</CardTitle>
                    <CardDescription>
                      Gerencie os usuários que têm acesso ao sistema
                    </CardDescription>
                  </div>
                  <Button 
                    className="bg-teal-600 hover:bg-teal-700"
                    onClick={() => {
                      setConvidarForm({ nome: "", email: "", cargo: "" });
                      setSenhaTemporaria("");
                      setConvidarDialogOpen(true);
                    }}
                    disabled={!podeConvidar}
                    data-testid="convidar-usuario-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Convidar Usuário
                  </Button>
                </div>
                
                {!podeConvidar && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Limite atingido!</strong> Seu plano <strong>{planoConfig.nome}</strong> permite até {limiteUsuarios} usuários. 
                      Entre em contato para fazer upgrade.
                    </p>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {/* Admin (você) */}
                <div className="mb-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {user?.nome?.charAt(0)?.toUpperCase() || "A"}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{user?.nome || "Administrador"}</p>
                        <p className="text-sm text-slate-500">{user?.email}</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-teal-600 text-white text-xs font-medium rounded-full">
                      Administrador
                    </span>
                  </div>
                </div>

                {/* Lista de usuários convidados */}
                {usuarios.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Nenhum usuário convidado ainda</p>
                    <p className="text-sm mt-1">Clique em "Convidar Usuário" para adicionar membros à sua equipe</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Usuário</TableHead>
                        <TableHead>Cargo</TableHead>
                        <TableHead>Adicionado em</TableHead>
                        <TableHead className="w-20">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usuarios.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                                <span className="text-slate-600 font-medium text-xs">
                                  {u.nome?.charAt(0)?.toUpperCase() || "U"}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{u.nome}</p>
                                <p className="text-xs text-slate-500">{u.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{u.cargo || "-"}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {formatDate(u.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleReenviarConvite(u.id)}
                                disabled={reenviandoConvite === u.id}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Reenviar convite com nova senha"
                              >
                                {reenviandoConvite === u.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4" />
                                )}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleDeleteUsuarioClick(u.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Remover usuário"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba Minha Farmácia */}
          <TabsContent value="empresa">
            <div className="space-y-6">
              {/* Logomarca */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="w-5 h-5 text-teal-600" />
                    Logomarca
                  </CardTitle>
                  <CardDescription>
                    A logomarca aparecerá nos relatórios e documentos gerados pelo sistema
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-6">
                    {/* Preview da logomarca */}
                    <div className="w-40 h-40 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
                      {logoUrl ? (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                          alt="Logomarca"
                          className="w-full h-full object-contain p-2"
                        />
                      ) : (
                        <div className="text-center text-slate-400">
                          <Image className="w-10 h-10 mx-auto mb-2" />
                          <p className="text-xs">Sem logomarca</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Controles */}
                    <div className="flex-1">
                      <p className="text-sm text-slate-600 mb-4">
                        Envie a logomarca da sua farmácia em formato PNG, JPG ou WEBP. 
                        Tamanho máximo: 5MB. Recomendamos uma imagem quadrada ou com fundo transparente.
                      </p>
                      
                      <input
                        type="file"
                        ref={logoInputRef}
                        onChange={handleUploadLogo}
                        accept=".png,.jpg,.jpeg,.webp"
                        className="hidden"
                      />
                      
                      <div className="flex gap-3">
                        <Button
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                          variant={logoUrl ? "outline" : "default"}
                          className={!logoUrl ? "bg-teal-600 hover:bg-teal-700" : ""}
                        >
                          {uploadingLogo ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          {logoUrl ? "Trocar Logomarca" : "Enviar Logomarca"}
                        </Button>
                        
                        {logoUrl && (
                          <Button
                            variant="outline"
                            onClick={handleRemoveLogo}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remover
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Informações do Plano */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-purple-600" />
                    Plano Atual
                  </CardTitle>
                  <CardDescription>
                    Informações sobre o seu plano de assinatura
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className={`px-4 py-2 rounded-full text-lg font-semibold ${planoConfig.cor}`}>
                          {planoConfig.nome}
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-sm text-slate-500">Usuários</p>
                        <p className="text-xl font-bold text-slate-900">
                          {usuariosCount} / {limiteUsuarios}
                        </p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-sm text-slate-500">Vagas disponíveis</p>
                        <p className="text-xl font-bold text-slate-900">
                          {Math.max(0, limiteUsuarios - usuariosCount)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                      <p className="text-sm text-teal-800">
                        <strong>Recursos incluídos:</strong> Documentos ilimitados, Verificações diárias, 
                        Gestão de funcionários, Relatórios em PDF, Alertas de vencimento.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Card Limpar Dados */}
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    <RefreshCw className="w-5 h-5" />
                    Limpar Dados
                  </CardTitle>
                  <CardDescription>
                    Remove todos os documentos, eventos, itens e verificações. Mantém a conta e usuários.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-4">
                    Use esta opção para recomeçar do zero. Todos os dados serão excluídos permanentemente, 
                    mas sua conta e usuários serão mantidos.
                  </p>
                  <Button 
                    variant="outline" 
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => setLimparDialogOpen(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Limpar Todos os Dados
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Aba Alertas */}
          <TabsContent value="alertas">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-teal-600" />
                  Alertas de Verificações Pendentes
                </CardTitle>
                <CardDescription>
                  Configure alertas automáticos por email quando verificações não forem realizadas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Ativar/Desativar */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="ativar-alertas"
                      checked={alertasConfig.ativo}
                      onCheckedChange={(checked) => setAlertasConfig({...alertasConfig, ativo: checked})}
                    />
                    <div>
                      <Label htmlFor="ativar-alertas" className="text-base font-medium cursor-pointer">
                        Ativar alertas por email
                      </Label>
                      <p className="text-sm text-slate-500">Receba notificações quando verificações não forem realizadas</p>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${alertasConfig.ativo ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                    {alertasConfig.ativo ? 'Ativo' : 'Inativo'}
                  </div>
                </div>

                {/* Destinatários */}
                <div className="space-y-4">
                  <h3 className="font-medium text-slate-900">Enviar alertas para:</h3>
                  
                  <div className="flex items-start gap-3 p-3 border rounded-lg">
                    <Checkbox
                      id="enviar-admin"
                      checked={alertasConfig.enviar_admin}
                      onCheckedChange={(checked) => setAlertasConfig({...alertasConfig, enviar_admin: checked})}
                      disabled={!alertasConfig.ativo}
                    />
                    <div>
                      <Label htmlFor="enviar-admin" className="cursor-pointer">
                        Email do administrador da farmácia
                      </Label>
                      <p className="text-sm text-slate-500">{user?.email}</p>
                    </div>
                  </div>

                  <div className="p-3 border rounded-lg space-y-3">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="enviar-adicional"
                        checked={alertasConfig.enviar_email_adicional}
                        onCheckedChange={(checked) => setAlertasConfig({...alertasConfig, enviar_email_adicional: checked})}
                        disabled={!alertasConfig.ativo}
                      />
                      <div className="flex-1">
                        <Label htmlFor="enviar-adicional" className="cursor-pointer">
                          Email adicional personalizado
                        </Label>
                        <p className="text-sm text-slate-500">Configure um email específico para receber os alertas</p>
                      </div>
                    </div>
                    
                    {alertasConfig.enviar_email_adicional && (
                      <div className="ml-6">
                        <Input
                          type="email"
                          placeholder="alertas@suafarmacia.com.br"
                          value={alertasConfig.email_adicional}
                          onChange={(e) => setAlertasConfig({...alertasConfig, email_adicional: e.target.value})}
                          disabled={!alertasConfig.ativo}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Horários */}
                <div className="space-y-4">
                  <h3 className="font-medium text-slate-900">Horários de verificação:</h3>
                  <p className="text-sm text-slate-500">Os alertas serão enviados nestes horários se houver verificações pendentes</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Alerta da manhã</Label>
                      <Input
                        type="time"
                        value={alertasConfig.horario_manha}
                        onChange={(e) => setAlertasConfig({...alertasConfig, horario_manha: e.target.value})}
                        disabled={!alertasConfig.ativo}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Alerta da tarde</Label>
                      <Input
                        type="time"
                        value={alertasConfig.horario_tarde}
                        onChange={(e) => setAlertasConfig({...alertasConfig, horario_tarde: e.target.value})}
                        disabled={!alertasConfig.ativo}
                      />
                    </div>
                  </div>
                </div>

                {/* Botões de ação */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                  <Button 
                    onClick={handleSalvarAlertas} 
                    disabled={salvandoAlertas}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    {salvandoAlertas ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Salvar Configurações
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={handleEnviarAlertaTeste} 
                    disabled={enviandoAlertaTeste || !alertasConfig.ativo}
                  >
                    {enviandoAlertaTeste ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Enviar Alerta de Teste
                  </Button>
                </div>

                {/* Info */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Como funciona:</strong> Nos horários configurados, o sistema verifica se há verificações 
                    de itens calibráveis ou rotinas personalizadas que ainda não foram realizadas. Se houver pendências, 
                    um email será enviado automaticamente para os destinatários configurados.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba Notificações Push */}
          <TabsContent value="push">
            <NotificationSettings isSuperAdmin={false} />
          </TabsContent>

          {/* Aba Indicação */}
          <TabsContent value="indicacao">
            <ReferralProgram />
          </TabsContent>

          {/* Aba Auditoria */}
          <TabsContent value="auditoria">
            <AuditLog />
          </TabsContent>
        </Tabs>

        {/* Dialog de Limpar Dados */}
        <Dialog open={limparDialogOpen} onOpenChange={setLimparDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Limpar Todos os Dados</DialogTitle>
              <DialogDescription>
                Esta ação é irreversível! Todos os dados serão excluídos permanentemente.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>Serão excluídos:</strong>
                </p>
                <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                  <li>Todos os documentos e alvarás</li>
                  <li>Todos os eventos sanitários</li>
                  <li>Todos os funcionários</li>
                  <li>Todos os itens calibráveis</li>
                  <li>Todas as verificações diárias</li>
                  <li>Todas as notas/lembretes</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="limpar-senha">Digite sua senha para confirmar</Label>
                <Input
                  id="limpar-senha"
                  type="password"
                  value={limparSenha}
                  onChange={(e) => setLimparSenha(e.target.value)}
                  placeholder="Sua senha"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="limpar-confirmar"
                  checked={limparConfirmar}
                  onCheckedChange={setLimparConfirmar}
                />
                <label htmlFor="limpar-confirmar" className="text-sm">
                  Confirmo que desejo excluir todos os dados permanentemente
                </label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setLimparDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleLimparDados}
                disabled={limpando || !limparSenha || !limparConfirmar}
              >
                {limpando ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Limpando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Limpar Dados
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de Convidar Usuário */}
        <Dialog open={convidarDialogOpen} onOpenChange={setConvidarDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Convidar Usuário</DialogTitle>
              <DialogDescription>
                O usuário receberá um email com as credenciais de acesso
              </DialogDescription>
            </DialogHeader>
            
            {senhaTemporaria ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium mb-2">Usuário convidado com sucesso!</p>
                  <p className="text-sm text-green-700">
                    As credenciais foram enviadas para o email informado.
                  </p>
                </div>
                
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 mb-2">
                    <strong>Senha temporária:</strong>
                  </p>
                  <code className="block p-2 bg-white rounded border text-lg font-mono">
                    {senhaTemporaria}
                  </code>
                  <p className="text-xs text-amber-600 mt-2">
                    Guarde essa senha caso o email não chegue. O usuário deverá alterá-la no primeiro acesso.
                  </p>
                </div>
                
                <DialogFooter>
                  <Button onClick={() => setConvidarDialogOpen(false)}>
                    Fechar
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome completo *</Label>
                    <Input
                      id="nome"
                      value={convidarForm.nome}
                      onChange={(e) => setConvidarForm({ ...convidarForm, nome: e.target.value })}
                      placeholder="Nome do usuário"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={convidarForm.email}
                      onChange={(e) => setConvidarForm({ ...convidarForm, email: e.target.value })}
                      placeholder="email@exemplo.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cargo">Cargo (opcional)</Label>
                    <Input
                      id="cargo"
                      value={convidarForm.cargo}
                      onChange={(e) => setConvidarForm({ ...convidarForm, cargo: e.target.value })}
                      placeholder="Ex: Farmacêutico, Auxiliar"
                    />
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setConvidarDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleConvidarUsuario} 
                    disabled={convidando}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    {convidando ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Convidando...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Enviar Convite
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Admin Password Dialog para exclusão de usuário */}
        <AdminPasswordDialog
          open={showDeletePasswordDialog}
          onOpenChange={(open) => {
            setShowDeletePasswordDialog(open);
            if (!open) setDeleteUsuarioId(null);
          }}
          onConfirm={handleRemoverUsuario}
          title="Remover Usuário"
          description="Tem certeza que deseja remover este usuário? Ele perderá o acesso ao sistema imediatamente."
        />
      </div>
    </Layout>
  );
};

export default Admin;
